import { useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import { lerp, easeInOut } from '../../lab/shared/PouringCylinder';

export { PouringCylinder, CylinderWaterStream } from '../../lab/shared/PouringCylinder';

interface SaltTransferProps {
  active: boolean;
  progress: number;
}

const DISH_REST_POS: [number, number, number] = [1.0, -0.46, 0.24];
const DISH_POUR_POS: [number, number, number] = [0.18, 1.06, -0.18];
const BEAKER_INSIDE: [number, number, number] = [0, 0.55, -0.2];

const DISH_RADIUS = 0.085;
const DISH_THICKNESS = 0.006;

const FALL_PARTICLE_COUNT = 70;

export function SaltTransferAnimation({ active, progress }: SaltTransferProps) {
  const fallRef = useRef<THREE.InstancedMesh>(null);
  const dummy = useMemo(() => new THREE.Object3D(), []);

  const seeds = useMemo(() => Array.from({ length: FALL_PARTICLE_COUNT }, () => ({
    speed: 0.55 + Math.random() * 0.55,
    phase: Math.random(),
    xJitter: (Math.random() - 0.5) * 0.04,
    zJitter: (Math.random() - 0.5) * 0.04,
    size: 0.005 + Math.random() * 0.004,
    spin: Math.random() * Math.PI * 2,
  })), []);

  let moveT = 0;
  if (progress < 0.25) moveT = progress / 0.25;
  else if (progress < 0.85) moveT = 1;
  else moveT = (1 - progress) / 0.15;
  moveT = easeInOut(Math.max(0, Math.min(1, moveT)));

  let tiltT = 0;
  if (progress >= 0.22 && progress < 0.32) tiltT = (progress - 0.22) / 0.10;
  else if (progress >= 0.32 && progress < 0.78) tiltT = 1;
  else if (progress >= 0.78 && progress < 0.88) tiltT = 1 - (progress - 0.78) / 0.10;
  tiltT = easeInOut(Math.max(0, Math.min(1, tiltT)));

  let saltAmount = 1;
  if (progress >= 0.30 && progress < 0.80) saltAmount = 1 - (progress - 0.30) / 0.50;
  else if (progress >= 0.80) saltAmount = 0;
  saltAmount = Math.max(0, saltAmount);

  const dishX = lerp(DISH_REST_POS[0], DISH_POUR_POS[0], moveT);
  const dishY = lerp(DISH_REST_POS[1], DISH_POUR_POS[1], moveT);
  const dishZ = lerp(DISH_REST_POS[2], DISH_POUR_POS[2], moveT);
  const tiltZ = lerp(0, 0.95, tiltT);

  const lipX = dishX + (-DISH_RADIUS) * Math.cos(tiltZ);
  const lipY = dishY + (-DISH_RADIUS) * Math.sin(tiltZ);
  const lipZ = dishZ;

  useFrame((state) => {
    if (!fallRef.current) return;
    const showStream = active && progress > 0.28 && progress < 0.88;

    if (!showStream) {
      for (let i = 0; i < FALL_PARTICLE_COUNT; i++) {
        dummy.position.set(0, -999, 0);
        dummy.scale.setScalar(0);
        dummy.updateMatrix();
        fallRef.current.setMatrixAt(i, dummy.matrix);
      }
      fallRef.current.instanceMatrix.needsUpdate = true;
      return;
    }

    const t = state.clock.elapsedTime;
    const intensity = 0.4 + 0.6 * saltAmount;
    const visible = Math.round(FALL_PARTICLE_COUNT * intensity);

    for (let i = 0; i < FALL_PARTICLE_COUNT; i++) {
      const s = seeds[i];
      if (i >= visible) {
        dummy.position.set(0, -999, 0);
        dummy.scale.setScalar(0);
      } else {
        const cycle = (t * s.speed + s.phase) % 1;
        const x = lerp(lipX, BEAKER_INSIDE[0], cycle) + s.xJitter + Math.sin(cycle * Math.PI) * -0.015;
        const y = lerp(lipY, BEAKER_INSIDE[1], cycle) - Math.sin(cycle * Math.PI) * 0.02;
        const z = lerp(lipZ, BEAKER_INSIDE[2], cycle) + s.zJitter;
        dummy.position.set(x, y, z);
        dummy.scale.setScalar(s.size);
        dummy.rotation.set(t * 1.4 + s.spin, t * 1.0, 0);
      }
      dummy.updateMatrix();
      fallRef.current.setMatrixAt(i, dummy.matrix);
    }
    fallRef.current.instanceMatrix.needsUpdate = true;
  });

  if (!active) return null;

  const pileShiftX = -0.045 * tiltT;
  const pileBaseR = 0.062 * Math.sqrt(saltAmount);
  const pileHeight = 0.028 * saltAmount * 2;

  return (
    <>
      <group position={[dishX, dishY, dishZ]} rotation={[0, 0, tiltZ]}>
        <mesh castShadow receiveShadow>
          <cylinderGeometry args={[DISH_RADIUS, DISH_RADIUS, DISH_THICKNESS, 32]} />
          <meshStandardMaterial color="#d6dde6" metalness={0.55} roughness={0.35} />
        </mesh>
        <mesh position={[0, DISH_THICKNESS / 2 - 0.0005, 0]}>
          <torusGeometry args={[DISH_RADIUS - 0.002, 0.0035, 10, 36]} />
          <meshStandardMaterial color="#9aa4b1" metalness={0.6} roughness={0.4} />
        </mesh>
        {saltAmount > 0.02 && (
          <mesh position={[pileShiftX, DISH_THICKNESS / 2 + pileHeight / 2, 0]}>
            <coneGeometry args={[pileBaseR, pileHeight, 24]} />
            <meshStandardMaterial color="#fafafa" roughness={0.95} />
          </mesh>
        )}
      </group>

      <instancedMesh ref={fallRef} args={[undefined, undefined, FALL_PARTICLE_COUNT]} castShadow>
        <boxGeometry args={[1, 1, 1]} />
        <meshStandardMaterial color="#fafafa" roughness={0.85} metalness={0.05} />
      </instancedMesh>
    </>
  );
}
